import { CaretDown, List, Phone, X } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { business, services } from '../content/site';
import { getNavigationServices } from '../lib/serviceDisplay';
import { BrandMark } from './BrandMark';
import { ScrollProgress } from './ScrollProgress';

const mainLinks = [
  { to: '/projects', label: 'Projects' },
  { to: '/service-area', label: 'Service Area' },
  { to: '/about', label: 'About' },
  { to: '/guides', label: 'Guides' },
];

export function Header() {
  const navigationServices = getNavigationServices(services);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const serviceMenu = useRef(null);
  const navigation = useRef(null);
  const menuToggle = useRef(null);
  const close = () => {
    setOpen(false);
    if (serviceMenu.current) serviceMenu.current.open = false;
  };

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      setScrolled(window.scrollY > 12);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (serviceMenu.current?.open && !serviceMenu.current.contains(event.target)) {
        serviceMenu.current.open = false;
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        close();
        menuToggle.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const background = [
      document.querySelector('main'),
      document.querySelector('.site-footer'),
      document.querySelector('.mobile-actions'),
    ].filter(Boolean);
    document.body.style.overflow = 'hidden';
    background.forEach((element) => { element.inert = true; });

    const focusFirst = window.requestAnimationFrame(() => navigation.current?.querySelector('summary')?.focus());
    const trapFocus = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = [menuToggle.current, ...navigation.current.querySelectorAll('a, summary, button, input, select')]
        .filter((element) => element && element.offsetParent !== null && !element.disabled);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapFocus);
    return () => {
      window.cancelAnimationFrame(focusFirst);
      document.body.style.overflow = previousOverflow;
      background.forEach((element) => { element.inert = false; });
      document.removeEventListener('keydown', trapFocus);
    };
  }, [open]);

  return (
    <header className={`site-header${scrolled ? ' is-scrolled' : ''}`}>
      <div className="site-header__inner">
        <BrandMark />
        <nav ref={navigation} id="mobile-navigation" className={`main-nav${open ? ' is-open' : ''}`} aria-label="Primary navigation">
          <details ref={serviceMenu} className="service-menu">
            <summary>Services <CaretDown aria-hidden="true" size={14} weight="bold" /></summary>
            <div className="service-menu__panel">
              <Link to="/services" onClick={close}><strong>All Services</strong><span>See the full service overview</span></Link>
              {navigationServices.map((service) => (
                <Link key={service.slug} to={`/${service.slug}`} onClick={close}>
                  <strong>{service.navName}</strong>
                  <span>{service.kicker}</span>
                </Link>
              ))}
            </div>
          </details>
          {mainLinks.map((link) => (
            <NavLink key={link.to} to={link.to} onClick={close}>{link.label}</NavLink>
          ))}
          <a className="main-nav__phone" href={business.phoneHref}><Phone aria-hidden="true" size={17} weight="fill" /> {business.phoneDisplay}</a>
          <Link className="button button--primary main-nav__cta" to="/contact" onClick={close}>Request an Estimate</Link>
        </nav>
        <button
          ref={menuToggle}
          className="menu-toggle"
          type="button"
          aria-expanded={open}
          aria-controls="mobile-navigation"
          aria-label={open ? 'Close navigation' : 'Open navigation'}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X aria-hidden="true" size={24} /> : <List aria-hidden="true" size={24} />}
        </button>
      </div>
      <ScrollProgress />
    </header>
  );
}
